import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';

import { TagsApi } from '@app/tags/tag-api.model';

import { AuthService } from '@app/shared/services/auth.service';
import { AuthUser } from '@app/account/account-api.model';
import { TranslateService } from '@ngx-translate/core';
import { TagFlagsService } from '@app/tags/services/tag-flags.service';
import { getAverageRatingLevel } from '@app/shared/utils/tag-helpers';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { isKey, Key } from '@app/shared/key';

@Component({
  selector: 'dgx-stacked-skill-ratings',
  templateUrl: './stacked-skill-ratings.component.html',
  styleUrls: ['./stacked-skill-ratings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StackedSkillRatingsComponent implements OnInit {
  // Bindings
  @Input() public skill: TagsApi.TagDetails;
  @Input() public isOwner: boolean;

  public sortedSkillRatings: TagsApi.UserTagRating[];
  public extrasLabel: string = '';
  public authUser: AuthUser;
  public userLoggedIn: boolean = false;
  public extrasTooltipText: string = '';

  private maxDonutRatings: number = 3;
  private maxExtraRatings: number = 99;
  private ratingTypeOrder = [
    'Credential',
    'Evaluation',
    'Manager',
    'Peer',
    'Self',
  ];
  public i18n = this.translateService.instant(['dgTagRating_AddRating']);

  constructor(
    private translateService: TranslateService,
    private auth: AuthService,
    private tagFlagsService: TagFlagsService,
    private tagRatingService: TagRatingService
  ) {}

  public get skillRatingsLabel(): string {
    return this.translateService.instant(
      'dgProfileOverview_StackedSkillRatingsAriaLabel',
      {
        skillName: this.skill.name,
      }
    );
  }

  public ngOnInit(): void {
    this.authUser = this.auth.authUser;
    this.userLoggedIn = !!this.authUser;

    // Don't show incomplete, in progress ratings, or multiple peer ratings
    const completedRatings = this.setRatings();

    // Only show Degreed ratings as ratings donuts
    // Ignoring goal ratings for now as we don't have the components to render them see PD-73834
    const degreedRatings = completedRatings.filter(
      (rating) =>
        rating.provider.providerCode === 'Degreed' && rating.type !== 'Goal'
    );

    // Sort the Degreed ratings in order of highest to lowest ratings level,
    // for ratings that have the same level sort according to this.ratingTypeOrder.
    // Only show this.maxDonutRatings number of ratings.
    this.sortedSkillRatings = degreedRatings
      .sort((a, b) => {
        if (a.level < b.level) {
          return 1;
        }
        if (a.level > b.level) {
          return -1;
        }
        if (a.level === b.level) {
          const aTypeIndex = this.ratingTypeOrder.findIndex(
            (ratingType) => ratingType === a.type
          );
          const bTypeIndex = this.ratingTypeOrder.findIndex(
            (ratingType) => ratingType === b.type
          );
          if (aTypeIndex < bTypeIndex) {
            return -1;
          }
          if (aTypeIndex > bTypeIndex) {
            return 1;
          }
          // Shouldn't get to here as you can't have multiple ratings of the same type on a single skill
          return 0;
        }
      })
      .slice(0, this.maxDonutRatings);

    // Extras only can include non Degreed ratings as long as they are complete
    const numExtras = completedRatings.length - this.sortedSkillRatings.length;
    this.extrasLabel = '';
    // Limit the extras display to this.maxExtraRatings
    if (numExtras > 0 && numExtras <= this.maxExtraRatings) {
      this.extrasLabel = `+${numExtras}`;
    } else if (numExtras > this.maxExtraRatings) {
      this.extrasLabel = `${numExtras}+`;
    }

    if (numExtras === 1) {
      this.extrasTooltipText = this.translateService.instant(
        'dgTagRating_SignalCountSingle',
        { count: numExtras }
      );
    } else {
      this.extrasTooltipText = this.translateService.instant(
        'dgTagRating_SignalCountPlural',
        { count: numExtras }
      );
    }
  }

  public openAddRatingModal(event: Event, tag: TagsApi.Tag) {
    event.stopPropagation();
    this.tagRatingService.openSkillModal(event, tag);
  }

  public keydownHandler(event: KeyboardEvent, tag: TagsApi.Tag): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling screen
      event.preventDefault();
      this.openAddRatingModal(event as Event, tag);
    }
  }

  public trackBy(index: number, rating: TagsApi.UserTagRating): number {
    return rating.userTagRatingId;
  }

  private setRatings(): TagsApi.UserTagRating[] {
    // Don't show incomplete or in progress ratings
    // Ignoring goal ratings for now as we don't have the components to render them see PD-73834
    let completedRatings = this.skill.ratings.filter(
      (rating) => rating.level !== undefined && rating.type !== 'Goal'
    );

    const peerRatings = completedRatings.filter(
      (rating) => rating.type === 'Peer'
    );

    // Only show 1 peer rating at a time
    if (peerRatings.length > 1) {
      completedRatings = completedRatings.filter(
        (rating) => rating.type !== 'Peer'
      );
      peerRatings[0].level = getAverageRatingLevel(peerRatings);
      completedRatings.push(peerRatings[0]);
    }

    return completedRatings;
  }
}
